import { useMemo } from "react";

import { calculateFile } from "~served/utils/calculations";

import {
	useTillCreateFileInput,
	useTillFileInputSnapshot,
	useTillUpdateFileInput,
} from "~/store";
import { FileProductInput } from "~/types/__generated/gql/graphql";

export const useTillSummary = () => {
	const createFileInput = useTillCreateFileInput();
	const updateFileInput = useTillUpdateFileInput();
	const snapshotFileInput = useTillFileInputSnapshot();

	const createFileInputSummary = useMemo(
		() =>
			calculateFile({
				products: createFileInput?.products ?? [],
				cancelledProducts: createFileInput?.cancelledProducts ?? [],
			}),
		[createFileInput],
	);

	const updateFileInputSummary = useMemo(
		() =>
			calculateFile({
				products: updateFileInput?.products ?? [],
				cancelledProducts: updateFileInput?.cancelledProducts ?? [],
			}),
		[updateFileInput],
	);

	const snapshotFileInputSummary = useMemo(
		() =>
			calculateFile({
				products: snapshotFileInput?.products ?? [],
				cancelledProducts: snapshotFileInput?.cancelledProducts ?? [],
			}),
		[snapshotFileInput],
	);

	const tillItemsSummary = useMemo(() => {
		const editFileInput = createFileInput ? createFileInput : updateFileInput;
		const added: FileProductInput[] = [] as FileProductInput[];
		const removed: FileProductInput[] = [] as FileProductInput[];

		for (const product of editFileInput?.products ?? []) {
			const matched = snapshotFileInput?.products?.find(
				(p) => p._id === product._id,
			);
			// if matched then both snapshot and on till has the product
			// else then the file on till has but not on snapshot, thus add.
			if (matched) {
				if (product.quantity > matched.quantity) {
					added.push({
						...matched,
						quantity: Math.abs(matched.quantity - product.quantity),
					});
				} else if (matched.quantity > product.quantity) {
					removed.push({
						...matched,
						quantity: Math.abs(matched.quantity - product.quantity),
					});
				}
			} else {
				added.push(product);
			}
		}

		return {
			prev: calculateFile({
				products: snapshotFileInput?.products ?? [],
				cancelledProducts: snapshotFileInput?.cancelledProducts ?? [],
			}),
			add: calculateFile({
				products: added ?? [],
				cancelledProducts: [],
			}),
			remove: calculateFile({
				products: removed ?? [],
				cancelledProducts: [],
			}),
		};
	}, [snapshotFileInput, createFileInput, updateFileInput]);

	return {
		createFileInputSummary,
		updateFileInputSummary,
		snapshotFileInputSummary,
		tillItemsSummary,
	};
};
